import React, { useEffect, useRef, useState } from "react";
import { BiSelectMultiple } from "react-icons/bi";
import { FaMinus, FaPlus } from "react-icons/fa";
import { MdArrowDropDown, MdClose } from "react-icons/md";

interface FilterOption {
  value: string;
  name: string;
}

interface MultiSelectProps {
  filterOptions: FilterOption[];
  onChange: (values: string[]) => void;
  filterName: string;
  selectAll?: boolean;
  preSelectedFilters?: string[];
}

const MultiSelect = ({
  filterOptions,
  onChange,
  filterName,
  selectAll = false,
  preSelectedFilters = [],
}: MultiSelectProps) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(() => {
    if (selectAll) {
      return filterOptions.map((option) => option.value);
    } else if (preSelectedFilters && preSelectedFilters.length > 0) {
      return preSelectedFilters;
    } else {
      return [];
    }
  });
  const [allSelected, setAllSelected] = useState(() => {
    if (selectAll) {
      return true;
    } else if (preSelectedFilters && preSelectedFilters.length > 0) {
      return preSelectedFilters.length === filterOptions.length;
    } else {
      return false;
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelectOption = (value: string) => {
    setSelectedOptions((prevSelected) => {
      const newSelectedOptions = prevSelected.includes(value)
        ? prevSelected.filter((item) => item !== value)
        : [...prevSelected, value];
      if (newSelectedOptions.length === filterOptions.length) {
        setAllSelected(true);
        onChange(["all"]);
      } else {
        setAllSelected(false);
        onChange(newSelectedOptions);
      }
      return newSelectedOptions;
    });
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // useEffect(() => {
  //   if (selectAll) {
  //     setSelectedOptions(filterOptions.map((option) => option.value));
  //     onChange(["all"]);
  //     setAllSelected(true);
  //   }
  // }, []);

  // useEffect(()=>{
  //   if (preSelectedFilters && preSelectedFilters.length > 0) {
  //       setSelectedOptions(preSelectedFilters.map((option) => option));
  //       onChange(preSelectedFilters.map((option) => option));
  //       setAllSelected(preSelectedFilters.length === filterOptions.length);
  //     }
  // },[preSelectedFilters]);

  const toggleAllSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (allSelected) {
      setSelectedOptions([]);
      onChange([]);
    } else {
      setSelectedOptions(filterOptions.map((option) => option.value));
      onChange(["all"]);
    }
    setAllSelected(!allSelected);
  };

  return (
    <div
      onClick={() => setIsOpen(!isOpen)}
      ref={dropdownRef}
      role="button"
      tabIndex={0}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-label={`Filter by ${filterName}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsOpen(!isOpen);
        }
        if (e.key === "Escape") setIsOpen(false);
      }}
      className="relative flex w-56 cursor-pointer items-center rounded-md border border-clinic-300 bg-white px-2.5 py-1.5 transition-colors hover:bg-clinic-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500 focus-visible:ring-offset-2 motion-reduce:transition-none"
    >
      <div className="flex w-full items-center justify-between gap-x-4">
        <h3
          className={`text-sm leading-tight ${
            selectedOptions.length > 0
              ? "font-medium tabular-nums text-clinic-900"
              : "text-clinic-500"
          }`}
        >
          {selectedOptions.length > 0
            ? `${selectedOptions.length} Selected`
            : `Filter By ${filterName}`}{" "}
        </h3>
        <div className="flex items-center justify-center gap-x-1.5 text-clinic-400">
          <span
            onClick={toggleAllSelection}
            role="button"
            aria-label={allSelected ? "Clear all" : "Select all"}
            className="cursor-pointer hover:text-clinic-600"
          >
            {allSelected ? (
              <MdClose className="h-4 w-4" />
            ) : (
              <BiSelectMultiple className="h-4 w-4" />
            )}
          </span>
          <MdArrowDropDown
            aria-hidden="true"
            className={`h-5 w-5 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      <div>
        {isOpen && (
          <div className="absolute top-full left-0 z-10 mt-1 max-h-[60vh] w-full overflow-y-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-clinic-200">
            {filterOptions.map((option) => (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectOption(option.value);
                }}
                key={option.value}
                className="flex cursor-pointer items-center justify-between px-3 py-1.5 hover:bg-clinic-100"
              >
                <span className="text-sm text-clinic-700">{option.name}</span>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    className="accent-[#0e7490]"
                    checked={selectedOptions.includes(option.value)}
                    readOnly
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelect;
